import { entries, exhaustiveCheck, get, isObject } from '@direct-frontend/stdlib';
import { UnexpectedError } from '@adv-frontend/error';
import { BaseField, BaseFields } from '../../fields/types';
import { parseFieldKey } from '../../fields/utils/parseFieldKey';
import { isArrayItemId } from './arrayItemId';
import { stringifyFieldIdPath } from '../../fields/utils/stringifyFieldIdPath';
import { getFieldPathById } from './getFieldPathByFieldId';
import { FormSliceState } from '../types';

interface GetDeclarationPathByIdProps {
    fieldsState: FormSliceState['forms'][string]['fieldsState'];
    fields: BaseFields;
    fieldId: string;
    formValue: unknown;
}

function getDeclarationPathById({ fieldId, fields, formValue, fieldsState }: GetDeclarationPathByIdProps) {
    // Разворачиваем путь чтобы можно было с конца доставать последовательно части
    const fieldIdPathReversed = parseFieldKey(fieldId).reverse();

    const currentIdPath: string[] = [];
    const declarationPath: string[] = [];

    let currentFields = fields;

    while (fieldIdPathReversed.length) {
        const key = fieldIdPathReversed.pop();

        // Сужение типов
        if (!key) {
            break;
        }

        currentIdPath.push(key);
        declarationPath.push(key);

        const field = currentFields[key];

        if (!field) {
            console.warn(
                new UnexpectedError('Не найдено поле', {
                    cause: null,
                    additional: {
                        key,
                        currentIdPath,
                        declarationPath,
                        fieldId,
                    },
                })
            );

            break;
        }

        function calculateFieldDeclarationPath(field: BaseField) {
            if (field.type === 'union') {
                // Если нет обращения к полю constituent'а, то далее не обрабатываем путь
                if (!fieldIdPathReversed.length) {
                    return;
                }

                const fieldPath = getFieldPathById(() => fieldsState, stringifyFieldIdPath(currentIdPath));
                // Достаем текущее значение union поля
                const fieldValue = get(formValue, fieldPath);

                if (!isObject(fieldValue)) {
                    console.warn(
                        new UnexpectedError('Ожидали объект для значения union поля', {
                            cause: null,
                            additional: {
                                fieldValue: JSON.stringify(fieldValue),
                                currentIdPath,
                                fieldPath,
                            },
                        })
                    );

                    return;
                }

                // Значение ключа
                const keyValue = fieldValue[field.key];

                // Ищем соответствующий constituent
                const constituentWithIndex = entries(field.constituents).find(
                    ([_, constituent]) => constituent.fields[field.key]?.defaultValue === keyValue
                );

                if (!constituentWithIndex) {
                    console.warn(
                        new UnexpectedError('Не найден constituent для union поля', {
                            cause: null,
                            additional: {
                                fieldValue: JSON.stringify(fieldValue),
                                currentFields: JSON.stringify(currentFields),
                            },
                        })
                    );

                    return;
                }

                const [index, constituent] = constituentWithIndex;

                declarationPath.push('constituents', String(index), 'fields');

                currentFields = constituent.fields;

                return;
            }

            if (field.type === 'object') {
                // а если нет обращения к полю объекта, то далее не обрабатываем путь
                if (!fieldIdPathReversed.length) {
                    return;
                }

                declarationPath.push('fields');

                currentFields = field.fields;

                return;
            }

            if (field.type === 'array') {
                // Убираем ARRAY_ITEM_ID_ABC из обрабатываемого пути
                const arrayItemId = fieldIdPathReversed.pop();

                // Обращения до элемента массива не происходит
                if (arrayItemId === undefined) {
                    return;
                }

                if (!isArrayItemId(arrayItemId)) {
                    console.warn(
                        new UnexpectedError('Не найден arrayItemId', {
                            cause: null,
                            additional: JSON.stringify({
                                fields,
                                fieldId,
                            }),
                        })
                    );
                }

                declarationPath.push('field');
                currentIdPath.push(arrayItemId);

                calculateFieldDeclarationPath(field.field);

                return;
            }

            if (field.type === 'primitive') {
                // Ничего не делаем

                return;
            }

            exhaustiveCheck(
                field,
                new UnexpectedError('Неизвестный тип поля', {
                    cause: null,
                })
            );
        }

        calculateFieldDeclarationPath(field);
    }

    return declarationPath;
}

interface GetFieldDeclarationByIdProps {
    /**
     * Значение fieldsState слайса
     */
    fieldsState: FormSliceState['forms'][string]['fieldsState'];
    /**
     * id поля, для которого получаем декларацию
     */
    fieldId: string;
    /**
     * Декларация полей формы
     */
    formFields: BaseFields;
    /**
     * Значение формы
     */
    formValue: unknown;
}

/**
 * Возвращает декларацию поля по его fieldId
 *
 * @returns декларацию поля соответвующего fieldId
 */
export function getFieldDeclarationById({
    fieldId,
    formValue,
    formFields,
    fieldsState,
}: GetFieldDeclarationByIdProps): BaseField | undefined {
    const declarationPath = getDeclarationPathById({ fieldId, fields: formFields, formValue, fieldsState });

    return get(formFields, declarationPath);
}
